import { Fragment, useState, useEffect, useRef } from "react";
import Paginator from "react-hooks-paginator";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { getSortedProducts } from "../../helpers/product";
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import Breadcrumb from "../../wrappers/breadcrumb/Breadcrumb";
import ShopSidebar from "../../wrappers/product/ShopSidebar";
import ShopTopbar from "../../wrappers/product/ShopTopbar";
import ShopProducts from "../../wrappers/product/ShopProducts";
import { useProducts } from "../../hooks/productsHooks/useProducts";
import { setActiveSort } from "../../helpers/product";
import MobileFilter from "./MobileFilter";
import { useTranslation } from "react-i18next";
import { useStoreInfo } from "../../hooks/storeInfoHooks/useStoreInfo";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useCategories } from "../../hooks/categoriesHooks/useCategories";
import {
  getPriceByCountry,
  getDiscountByCountry,
} from "../../helpers/countryHelper";

const Shop = () => {
  const minRef = useRef(null);
  const maxRef = useRef(null);
  const [min, setMin] = useState();
  const [max, setMax] = useState();
  const [categoriesFilter, setCategoriesFilter] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [layout, setLayout] = useState("grid three-column");
  const [sortType, setSortType] = useState("");
  const [search, setSearch] = useState("");
  const [sortValue, setSortValue] = useState("");
  const [brandsFilter, setBrandsFilter] = useState([]);
  const [ratingFilter, setRatingFilter] = useState([]);
  const [sort, setSort] = useState("");
  const [offset, setOffset] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [sortedProducts, setSortedProducts] = useState([]);
  const { data: products, isLoading } = useProducts();
  const { data: categories } = useCategories();
  const { data: storeInfo } = useStoreInfo();
  const { t, i18n } = useTranslation();
  const { selectedCountry } = useSelector((state) => state.country);
  const pageLimit = 20;
  const { id } = useParams();
  let { pathname } = useLocation();

  const getLayout = (layout) => {
    setLayout(layout);
  };

  const getSortParams = (sortType, sortValue) => {
    setSortType(sortType);
    setSortValue(sortValue);
  };

  const getFilterSortParams = (sortType, sortValue) => {
    setBrandsFilter(sortType);
    setSort(sortValue);
  };

  useEffect(() => {
    setCurrentData(
      products?.products
        ?.filter(
          (product) =>
            product?.category?.parent_id == id || product?.category?.id == id
        )
        ?.filter((product) =>
          search && i18n.language === "en"
            ? product.name_en?.toLowerCase()?.includes(search)
            : product.name?.toLowerCase()?.includes(search)
        )
        ?.filter((p) =>
          categoriesFilter?.length
            ? categoriesFilter.includes(p.category?.id) ||
              categoriesFilter.includes(p.category?.parent_id)
            : true
        )
        ?.filter((p) =>
          brandsFilter?.length ? brandsFilter.includes(p.brand?.id) : true
        )
        ?.filter((p) =>
          ratingFilter?.length
            ? ratingFilter.includes(Math.floor(Number(p.rating_avg)))
            : true
        )
        ?.filter((product) => {
          const countryPrice = getPriceByCountry(product, selectedCountry);
          const countryDiscount = getDiscountByCountry(
            product,
            selectedCountry
          );
          const finalPrice =
            countryPrice - (countryPrice * countryDiscount) / 100;
          return min ? finalPrice >= Number(min) : true;
        })
        ?.filter((product) => {
          const countryPrice = getPriceByCountry(product, selectedCountry);
          const countryDiscount = getDiscountByCountry(
            product,
            selectedCountry
          );
          const finalPrice =
            countryPrice - (countryPrice * countryDiscount) / 100;
          return max ? finalPrice <= Number(max) : true;
        })
        ?.sort((a, b) => {
          const priceA = getPriceByCountry(a, selectedCountry);
          const priceB = getPriceByCountry(b, selectedCountry);
          return (
            (!sort && a.id - b.id) ||
            (sort == "default" && a.id - b.id) ||
            (sort == "priceHighToLow" && priceB - priceA) ||
            (sort == "priceLowToHigh" && priceA - priceB) ||
            (sort == "dateOldToNew" &&
              new Date(a.updated_at) - new Date(b.updated_at)) ||
            (sort == "dateNewToOld" &&
              new Date(b.updated_at) - new Date(a.updated_at))
          );
        })
    );
  }, [
    offset,
    products,
    brandsFilter,
    sort,
    categoriesFilter,
    ratingFilter,
    min,
    max,
    search,
    id,
    selectedCountry,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  return (
    <Fragment>
      <SEO
        title={"shop"}
        titleTemplate={
          i18n?.language == "ar"
            ? categories?.find((p) => p?.id == id)?.name
            : categories?.find((p) => p?.id == id)?.name_en
        }
        description={storeInfo?.[0]?.description}
      />

      <LayoutOne headerTop="visible">
        {/* breadcrumb */}
        <Breadcrumb
          pages={[
            {
              label:
                i18n?.language == "ar"
                  ? categories?.find((p) => p?.id == id)?.name
                  : categories?.find((p) => p?.id == id)?.name_en,
              path: "/shop",
            },
            { label: "shop", path: "/shop", link: true },
            { label: "home", path: "/" },
          ]}
        />

        <div className="shop-area pt-95 pb-100">
          <div className="container ">
            <div className="row">
              <button
                onClick={() => setIsOpen(true)}
                className="d-md-none d-block btn rounded-circle bg-black text-white p-2 d-flex justify-content-center align-items-center position-fixed"
                style={{
                  bottom: "10px",
                  right: "50%",
                  transform: "translateX(50%)",
                  zIndex: 9999,
                  width: "45px",
                  height: "45px",
                }}
              >
                <svg
                  width={"22"}
                  height={"22"}
                  viewBox="0 0 24 24"
                  fill="black"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="SVGRepo_bgCarrier" strokeWidth={0} />
                  <g
                    id="SVGRepo_tracerCarrier"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <g id="SVGRepo_iconCarrier">
                    <path
                      d="M21 6H19M21 12H16M21 18H16M7 20V13.5612C7 13.3532 7 13.2492 6.97958 13.1497C6.96147 13.0615 6.93151 12.9761 6.89052 12.8958C6.84431 12.8054 6.77934 12.7242 6.64939 12.5617L3.35061 8.43826C3.22066 8.27583 3.15569 8.19461 3.10948 8.10417C3.06849 8.02393 3.03853 7.93852 3.02042 7.85026C3 7.75078 3 7.64677 3 7.43875V5.6C3 5.03995 3 4.75992 3.10899 4.54601C3.20487 4.35785 3.35785 4.20487 3.54601 4.10899C3.75992 4 4.03995 4 4.6 4H13.4C13.9601 4 14.2401 4 14.454 4.10899C14.6422 4.20487 14.7951 4.35785 14.891 4.54601C15 4.75992 15 5.03995 15 5.6V7.43875C15 7.64677 15 7.75078 14.9796 7.85026C14.9615 7.93852 14.9315 8.02393 14.8905 8.10417C14.8443 8.19461 14.7793 8.27583 14.6494 8.43826L11.3506 12.5617C11.2207 12.7242 11.1557 12.8054 11.1095 12.8958C11.0685 12.9761 11.0385 13.0615 11.0204 13.1497C11 13.2492 11 13.3532 11 13.5612V17L7 20Z"
                      stroke="white"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                </svg>
              </button>
              <MobileFilter
                minRef={minRef}
                maxRef={maxRef}
                min={min}
                max={max}
                setMin={setMin}
                setMax={setMax}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                getFilterSortParams={getFilterSortParams}
                products={products?.products}
                getSortParams={getSortParams}
                ratingFilter={ratingFilter}
                setRatingFilter={setRatingFilter}
                categoriesFilter={categoriesFilter}
                setCategoriesFilter={setCategoriesFilter}
                categoriesItems={categories}
                sortType={sortType}
                brandsFilter={brandsFilter}
                setBrandsFilter={setBrandsFilter}
                setSort={setSort}
                setSortType={setSortType}
                setSearch={setSearch}
                setSortValue={setSortValue}
                setActiveSort={setActiveSort}
              />
              <div className="col-md-3 order-1 d-md-block d-none">
                {/* shop sidebar */}
                <ShopSidebar
                  minRef={minRef}
                  maxRef={maxRef}
                  min={min}
                  max={max}
                  setMin={setMin}
                  setMax={setMax}
                  setSearch={setSearch}
                  brandsFilter={brandsFilter}
                  ratingFilter={ratingFilter}
                  setRatingFilter={setRatingFilter}
                  setSort={setSort}
                  categories={false}
                  setBrandsFilter={setBrandsFilter}
                  categoriesFilter={categoriesFilter}
                  setCategoriesFilter={setCategoriesFilter}
                  categoriesItems={categories}
                  getFilterSortParams={getFilterSortParams}
                  products={products?.products}
                  getSortParams={getSortParams}
                  sideSpaceClass="ml-30"
                />
                {sortType ||
                min ||
                max ||
                categoriesFilter?.length ||
                brandsFilter?.length ||
                ratingFilter?.length ? (
                  <button
                    className="btn btn-danger mt-5"
                    onClick={() => {
                      setSortType("");
                      setSortValue("");
                      setCategoriesFilter([]);
                      setBrandsFilter([]);
                      setRatingFilter([]);
                      setMax(null);
                      setMin(null);
                      setActiveSort("");
                    }}
                  >
                    {t("clear")}
                  </button>
                ) : (
                  ""
                )}
              </div>
              <div className="col-md-9 order-2">
                {/* shop topbar default */}
                <ShopTopbar
                  setSort={setSort}
                  getLayout={getLayout}
                  getFilterSortParams={getFilterSortParams}
                  productCount={products?.products?.length}
                  sortedProductCount={currentData?.length}
                />
                {/* shop page content default */}
                {isLoading && (
                  <>
                    <Skeleton
                      containerClassName="d-lg-flex d-none flex-nowrap gap-2"
                      className="w-120 flex-grow-1"
                      height={200}
                      count={4}
                    />
                    <Skeleton
                      containerClassName="d-lg-flex d-none flex-nowrap gap-2"
                      className="w-120 flex-grow-1 mt-3"
                      height={20}
                      count={4}
                    />
                    <Skeleton
                      containerClassName="d-lg-flex d-none flex-nowrap gap-2"
                      className="w-120 flex-grow-1  mt-3"
                      height={20}
                      count={4}
                    />
                    <Skeleton
                      containerClassName="d-md-flex d-none d-lg-none flex-nowrap gap-2"
                      className="w-120 flex-grow-1"
                      height={200}
                      count={3}
                    />
                    <Skeleton
                      containerClassName="d-md-flex d-none d-lg-none flex-nowrap gap-2"
                      className="w-120 flex-grow-1 mt-3"
                      height={20}
                      count={3}
                    />
                    <Skeleton
                      containerClassName="d-md-flex d-none d-lg-none flex-nowrap gap-2"
                      className="w-120 flex-grow-1  mt-3"
                      height={20}
                      count={3}
                    />
                    <Skeleton
                      containerClassName="d-flex d-md-none flex-nowrap gap-1"
                      className="w-100 flex-grow-1"
                      height={120}
                      count={2}
                    />
                    <Skeleton
                      containerClassName="d-flex d-md-none flex-nowrap gap-1"
                      className="w-100 flex-grow-1 mt-3"
                      height={20}
                      count={2}
                    />
                    <Skeleton
                      containerClassName="d-flex d-md-none flex-nowrap gap-1"
                      className="w-100 flex-grow-1  mt-3"
                      height={20}
                      count={2}
                    />
                  </>
                )}
                <ShopProducts
                  layout={layout}
                  products={currentData?.slice(offset, offset + pageLimit)}
                />
                {!currentData?.length &&
                (categoriesFilter?.length ||
                  search ||
                  ratingFilter?.length ||
                  brandsFilter?.length) ? (
                  <div className="d-flex justify-content-center flex-column align-items-center">
                    <svg
                      width={60}
                      height={60}
                      viewBox="0 -0.5 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth={0} />
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <g id="SVGRepo_iconCarrier">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M5.5 11.493C5.50364 8.39226 7.69698 5.72579 10.7388 5.12416C13.7807 4.52253 16.8239 6.15327 18.0077 9.0192C19.1915 11.8851 18.186 15.1881 15.6063 16.9085C13.0265 18.6288 9.59077 18.2874 7.4 16.093C6.18148 14.8725 5.49799 13.2177 5.5 11.493Z"
                          stroke="#000000"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />

                        <path
                          d="M16.062 16.568L19.5 19.993"
                          stroke="#000000"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />

                        <path
                          d="M10.5303 8.96271C10.2374 8.66982 9.76256 8.66982 9.46967 8.96271C9.17678 9.25561 9.17678 9.73048 9.46967 10.0234L10.5303 8.96271ZM11.4697 12.0234C11.7626 12.3163 12.2374 12.3163 12.5303 12.0234C12.8232 11.7305 12.8232 11.2556 12.5303 10.9627L11.4697 12.0234ZM12.5303 10.9627C12.2374 10.6698 11.7626 10.6698 11.4697 10.9627C11.1768 11.2556 11.1768 11.7305 11.4697 12.0234L12.5303 10.9627ZM13.4697 14.0234C13.7626 14.3163 14.2374 14.3163 14.5303 14.0234C14.8232 13.7305 14.8232 13.2556 14.5303 12.9627L13.4697 14.0234ZM12.5303 12.0234C12.8232 11.7305 12.8232 11.2556 12.5303 10.9627C12.2374 10.6698 11.7626 10.6698 11.4697 10.9627L12.5303 12.0234ZM9.46967 12.9627C9.17678 13.2556 9.17678 13.7305 9.46967 14.0234C9.76256 14.3163 10.2374 14.3163 10.5303 14.0234L9.46967 12.9627ZM11.4697 10.9627C11.1768 11.2556 11.1768 11.7305 11.4697 12.0234C11.7626 12.3163 12.2374 12.3163 12.5303 12.0234L11.4697 10.9627ZM14.5303 10.0234C14.8232 9.73048 14.8232 9.25561 14.5303 8.96271C14.2374 8.66982 13.7626 8.66982 13.4697 8.96271L14.5303 10.0234ZM9.46967 10.0234L11.4697 12.0234L12.5303 10.9627L10.5303 8.96271L9.46967 10.0234ZM11.4697 12.0234L13.4697 14.0234L14.5303 12.9627L12.5303 10.9627L11.4697 12.0234ZM11.4697 10.9627L9.46967 12.9627L10.5303 14.0234L12.5303 12.0234L11.4697 10.9627ZM12.5303 12.0234L14.5303 10.0234L13.4697 8.96271L11.4697 10.9627L12.5303 12.0234Z"
                          fill="#000000"
                        />
                      </g>
                    </svg>

                    <h3>{t("no_results_found")}!</h3>
                  </div>
                ) : (
                  ""
                )}
                {!products?.products?.filter(
                  (p) => p?.category?.parent_id == id || p?.category?.id == id
                )?.length && !isLoading ? (
                  <div className="d-flex justify-content-center flex-column align-items-center">
                    <svg
                      width={60}
                      height={60}
                      viewBox="0 -0.5 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g id="SVGRepo_bgCarrier" strokeWidth={0} />
                      <g
                        id="SVGRepo_tracerCarrier"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <g id="SVGRepo_iconCarrier">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M5.5 11.493C5.50364 8.39226 7.69698 5.72579 10.7388 5.12416C13.7807 4.52253 16.8239 6.15327 18.0077 9.0192C19.1915 11.8851 18.186 15.1881 15.6063 16.9085C13.0265 18.6288 9.59077 18.2874 7.4 16.093C6.18148 14.8725 5.49799 13.2177 5.5 11.493Z"
                          stroke="#000000"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />

                        <path
                          d="M16.062 16.568L19.5 19.993"
                          stroke="#000000"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />

                        <path
                          d="M10.5303 8.96271C10.2374 8.66982 9.76256 8.66982 9.46967 8.96271C9.17678 9.25561 9.17678 9.73048 9.46967 10.0234L10.5303 8.96271ZM11.4697 12.0234C11.7626 12.3163 12.2374 12.3163 12.5303 12.0234C12.8232 11.7305 12.8232 11.2556 12.5303 10.9627L11.4697 12.0234ZM12.5303 10.9627C12.2374 10.6698 11.7626 10.6698 11.4697 10.9627C11.1768 11.2556 11.1768 11.7305 11.4697 12.0234L12.5303 10.9627ZM13.4697 14.0234C13.7626 14.3163 14.2374 14.3163 14.5303 14.0234C14.8232 13.7305 14.8232 13.2556 14.5303 12.9627L13.4697 14.0234ZM12.5303 12.0234C12.8232 11.7305 12.8232 11.2556 12.5303 10.9627C12.2374 10.6698 11.7626 10.6698 11.4697 10.9627L12.5303 12.0234ZM9.46967 12.9627C9.17678 13.2556 9.17678 13.7305 9.46967 14.0234C9.76256 14.3163 10.2374 14.3163 10.5303 14.0234L9.46967 12.9627ZM11.4697 10.9627C11.1768 11.2556 11.1768 11.7305 11.4697 12.0234C11.7626 12.3163 12.2374 12.3163 12.5303 12.0234L11.4697 10.9627ZM14.5303 10.0234C14.8232 9.73048 14.8232 9.25561 14.5303 8.96271C14.2374 8.66982 13.7626 8.66982 13.4697 8.96271L14.5303 10.0234ZM9.46967 10.0234L11.4697 12.0234L12.5303 10.9627L10.5303 8.96271L9.46967 10.0234ZM11.4697 12.0234L13.4697 14.0234L14.5303 12.9627L12.5303 10.9627L11.4697 12.0234ZM11.4697 10.9627L9.46967 12.9627L10.5303 14.0234L12.5303 12.0234L11.4697 10.9627ZM12.5303 12.0234L14.5303 10.0234L13.4697 8.96271L11.4697 10.9627L12.5303 12.0234Z"
                          fill="#000000"
                        />
                      </g>
                    </svg>

                    <h3>{t("no_results_found")}!</h3>
                  </div>
                ) : (
                  ""
                )}
                {/* shop product pagination */}
                <div className="pro-pagination-style text-center mt-30">
                  <Paginator
                    totalRecords={currentData?.length}
                    pageLimit={pageLimit}
                    pageNeighbours={2}
                    setOffset={setOffset}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    pageContainerClass="mb-0 mt-0"
                    pagePrevText="«"
                    pageNextText="»"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </LayoutOne>
    </Fragment>
  );
};

export default Shop;
